import { addDays, differenceInYears, getDay, isWithinInterval } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { parseLocalDate, todayDate } from './date-formatter';

// True when a backend date string ("yyyy-MM-dd'Z'") falls inside the range, both ends included.
// parseLocalDate reads the trailing marker and yields the same local midnight the date pickers
// produce, so the row and the bounds are directly comparable. A row carrying no date is outside
// every range.
export const isDateStringWithinRange = (dateString: string | undefined, start: Date, end: Date): boolean =>
    !!dateString && isWithinInterval(parseLocalDate(dateString), { start, end });

export const sortByDate = (dateStringA?: string, dateStringB?: string) => {
    if (!dateStringA && !dateStringB) {
        return 0;
    }
    if (!dateStringA) {
        return -1;
    }
    if (!dateStringB) {
        return 1;
    }
    return parseLocalDate(dateStringA).getTime() - parseLocalDate(dateStringB).getTime();
};

export function getLatestValuationDate(positions?: Array<{ valuationDate: string }>) {
    if (positions == null || !positions.length) {
        return undefined;
    }
    const time = positions.reduce((latest: string | null, p) => {
        if (!p.valuationDate) {
            return latest;
        }
        if (!latest) {
            return p.valuationDate;
        }
        return new Date(latest).getTime() > new Date(p.valuationDate).getTime() ? latest : p.valuationDate;
    }, null);
    return time ? new Date(time) : new Date();
}

const FULL_DAY_EVENT_MS = 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000; // 23:59:59 in milliseconds

export function isFullDayEvent(start: Date, end: Date): boolean {
    return FULL_DAY_EVENT_MS <= end.getTime() - start.getTime();
}

/**
 * Convert a backend datetime string (assumed to represent time in `sourceTz`) to
 * a `Date` object adjusted to the browser's timezone.
 */
export function convertBackendTimeToBrowser(dateTime: string, sourceTz: string): Date {
    const browserTz = new Intl.DateTimeFormat().resolvedOptions().timeZone;

    // If the backend adds a trailing 'Z', strip it so the value is interpreted as
    // local time in `sourceTz`.
    const normalized = dateTime.endsWith('Z') ? dateTime.slice(0, -1) : dateTime;

    // Local (`sourceTz`) → UTC
    const utcDate = zonedTimeToUtc(normalized, sourceTz);

    // UTC → browser tz
    return utcToZonedTime(utcDate, browserTz);
}

/**
 * Returns the next working day (Mon–Fri) after today.
 * If today is Friday, returns Monday; if Saturday, returns Monday; otherwise returns tomorrow.
 */
export const nextWorkingDay = (): Date => {
    const today = todayDate();
    const day = getDay(today); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    const daysToAdd = day >= 5 ? 8 - day : 1; // Fri(5)->3, Sat(6)->2, else->1
    return addDays(today, daysToAdd);
};

/**
 * Calculate age from a date of birth string
 * @param dateOfBirth - Date of birth string in ISO format
 * @returns Age in years, or undefined if dateOfBirth is not provided
 */
export function calculateAge(dateOfBirth?: string): number | undefined {
    if (!dateOfBirth) {
        return undefined;
    }
    try {
        return differenceInYears(new Date(), parseLocalDate(dateOfBirth));
    } catch (error) {
        return undefined;
    }
}
